// Remembers which knowledge base the onboarding wizard is currently building
// (dataset id + the documents in it) so a page refresh can resume watching
// it instead of dropping the user back on step 1, where re-picking files
// would create a second dataset — the Knowledge API accepts duplicate names,
// and every copy indexes (and bills) separately.
//
// localStorage rather than sessionStorage so it also survives the tab being
// closed and reopened. Account-scoped (a multi-account admin must never
// resume another account's dataset) and time-limited (an abandoned attempt
// shouldn't redirect the user into a stale dataset days later). Every access
// is wrapped: storage can throw (private window, blocked site data) and the
// wizard must still work without it, just without resume.
const STORE_PREFIX = 'onboarding_kb_session';
const EXPIRY_MS = 24 * 60 * 60 * 1000;

const storageKey = accountId => `${STORE_PREFIX}::${accountId}`;

// `staged` is the files the user chose to upload (name + size only; the File
// objects themselves cannot survive a refresh). It lets a restore name any
// chosen file that never reached the server instead of silently omitting it.
export const saveOnboardingKbSession = (
  accountId,
  { datasetId, docs, staged, dropped }
) => {
  if (!accountId || !datasetId) return;
  try {
    window.localStorage.setItem(
      storageKey(accountId),
      JSON.stringify({
        datasetId,
        docs: (docs || []).map(({ id, name, size }) => ({ id, name, size })),
        staged: (staged || []).map(({ name, size }) => ({ name, size })),
        // Files the user chose that are not in the knowledge base (see the
        // 'dropped' status in the store). Kept so that a second refresh does
        // not make the notice about them disappear.
        dropped: (dropped || []).map(({ name, size, reason }) => ({
          name,
          size,
          reason,
        })),
        expiresAt: Date.now() + EXPIRY_MS,
      })
    );
  } catch (error) {
    // Resume is a convenience — losing it must never break the wizard.
  }
};

export const getOnboardingKbSession = accountId => {
  if (!accountId) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(accountId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.datasetId || !(parsed.expiresAt > Date.now())) {
      window.localStorage.removeItem(storageKey(accountId));
      return null;
    }
    return {
      datasetId: parsed.datasetId,
      docs: parsed.docs || [],
      staged: parsed.staged || [],
      dropped: parsed.dropped || [],
    };
  } catch (error) {
    return null;
  }
};

export const clearOnboardingKbSession = accountId => {
  if (!accountId) return;
  try {
    window.localStorage.removeItem(storageKey(accountId));
  } catch (error) {
    // Nothing to clean up if storage is unavailable.
  }
};
